import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, Eyebrow, Reveal } from "./motion";

const MENU = [
    {
        id: "facials",
        name: "Facials",
        items: [
            { name: "Signature Facial", price: "$99" },
            { name: "Deep Cleansing Facial", price: "$107" },
            { name: "Hydrating Glow Facial", price: "$111" },
            { name: "Luxury Facial", price: "$149" },
        ],
    },
    {
        id: "targeted",
        name: "Targeted Treatment",
        items: [
            { name: "Acne Facial", price: "$120" },
            { name: "Brightening Facial", price: "$115" },
            { name: "Anti-Ageing Facial", price: "$145" },
            { name: "Back Facial", price: "$100" },
        ],
    },
    {
        id: "advanced",
        name: "Advanced Treatment",
        items: [
            { name: "Dermaplaning", price: "$110" },
            { name: "Medical Peel", price: "from $135" },
            { name: "Microneedling", price: "$289" },
            { name: "Bio-Repeel", price: "from $249" },
        ],
    },
    {
        id: "express",
        name: "Express Treatment",
        items: [
            { name: "Express Facial · 30 mins", price: "$75" },
            { name: "Exosomes", price: "$139" },
            { name: "Hydrating Mask Treatment", price: "$49" },
        ],
    },
    {
        id: "waxing",
        name: "Waxing",
        items: [
            { name: "Brows", price: "$55" },
            { name: "Full Face", price: "$44" },
            { name: "Underarms", price: "$29" },
            { name: "Upper Lip", price: "$15" },
            { name: "Chin", price: "$12" },
        ],
    },
];

const ENHANCEMENTS = [
    { name: "Brow Tint", price: "+$15" },
    { name: "Hydrating Mask", price: "+$25" },
    { name: "Eye Treatment", price: "+$20" },
    { name: "Lip Treatment", price: "+$20" },
    { name: "Lymphatic Drainage", price: "+$45" },
];

const MAILTO =
    "mailto:hello@inskinau.com.au?subject=Treatment%20enquiry%20%E2%80%94%20In%20Skin";

export default function Treatments() {
    const [active, setActive] = useState(MENU[0].id);
    const current = MENU.find((m) => m.id === active);

    return (
        <section
            id="treatments"
            data-testid="treatments-section"
            className="bg-cream px-6 py-24 md:px-12 md:py-36"
        >
            <div className="mx-auto max-w-[1440px]">
                <div className="flex flex-col justify-between gap-10 md:flex-row md:items-end">
                    <div>
                        <Eyebrow index="03">The treatment menu</Eyebrow>
                        <Reveal delay={0.1}>
                            <h2 className="mt-8 max-w-2xl font-serif text-[clamp(2.2rem,4.5vw,4.25rem)] font-light leading-[1.05] text-ink">
                                Considered treatments,
                                <br />
                                <span className="italic text-earth">honest results.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-xs text-sm font-light leading-relaxed text-earth">
                            Every protocol is bespoke — your final treatment plan
                            is confirmed in consultation.
                        </p>
                    </Reveal>
                </div>

                {/* Category tabs */}
                <Reveal delay={0.25}>
                    <div className="mt-12 inline-flex flex-wrap gap-1 rounded-full bg-linen p-1.5">
                        {MENU.map((m) => (
                            <button
                                key={m.id}
                                data-testid={`treatment-category-tab-${m.id}`}
                                onClick={() => setActive(m.id)}
                                className={`relative rounded-full px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 md:text-[11px] ${
                                    active === m.id ? "text-cream" : "text-earth hover:text-ink"
                                }`}
                            >
                                {active === m.id && (
                                    <motion.span
                                        layoutId="treatment-tab-pill"
                                        className="absolute inset-0 rounded-full bg-ink"
                                        transition={{ duration: 0.5, ease: EASE }}
                                    />
                                )}
                                <span className="relative z-10">{m.name}</span>
                            </button>
                        ))}
                    </div>
                </Reveal>

                {/* Items */}
                <div className="mt-10 md:mt-14">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            data-testid="treatment-list"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.5, ease: EASE }}
                            className="border-t border-ink/10"
                        >
                            {current.items.map((it) => (
                                <a
                                    key={it.name}
                                    data-testid="treatment-item-card"
                                    href={MAILTO}
                                    className="group flex items-baseline justify-between gap-6 border-b border-ink/10 px-2 py-7 transition-colors duration-300 hover:bg-linen/70 md:px-6 md:py-8"
                                >
                                    <h3 className="font-serif text-2xl font-light leading-snug text-ink transition-colors duration-300 group-hover:text-earth md:text-3xl">
                                        {it.name}
                                    </h3>
                                    <span className="flex items-baseline gap-3">
                                        <span className="font-serif text-2xl font-light italic text-ink md:text-3xl">
                                            {it.price}
                                        </span>
                                        <ArrowUpRight
                                            size={20}
                                            strokeWidth={1.5}
                                            className="hidden text-clay opacity-0 transition-all duration-300 group-hover:opacity-100 md:block"
                                        />
                                    </span>
                                </a>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-16" data-testid="treatment-enhancements">
                        <h3 className="font-serif text-2xl font-light italic text-ink md:text-3xl">
                            Enhance any treatment
                        </h3>
                        <div className="mt-6 flex flex-wrap gap-3">
                            {ENHANCEMENTS.map((e) => (
                                <span
                                    key={e.name}
                                    data-testid="treatment-enhancement-chip"
                                    className="rounded-full border border-ink/15 bg-linen/60 px-5 py-2.5 text-sm font-light text-earth"
                                >
                                    {e.name} <span className="ml-1 text-ink">{e.price}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                            Waxing packages available in studio · Prices in Australian dollars
                        </p>
                        <a
                            data-testid="treatments-book-online-button"
                            href={MAILTO}
                            className="rounded-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-earth hover:shadow-xl"
                        >
                            Book online
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
